import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Dock } from "./Dock";
import { ShieldAlert, LogOut } from "lucide-react";
import { handleLogout } from "../auth/logout";

const MainLayout: React.FC = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {

    const requiredStorageKey = "permissions";


    if (localStorage.getItem(requiredStorageKey)) {
      setIsReady(true);
      return;
    }

    const interval = setInterval(() => {
      if (localStorage.getItem(requiredStorageKey)) {
        setIsReady(true);
        clearInterval(interval);
      }
    }, 50); // Check every 50ms 

    const timeout = setTimeout(() => {
      clearInterval(interval);
      setIsReady(true);
    }, 2000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-gray-900">
        <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const userInfo = localStorage.getItem('user');
  const user = userInfo ? JSON.parse(userInfo) : null;
  const isSSOUser = user?.role === 'sso_user' || user?.role?.toLowerCase() === 'sso_user';

  if (isSSOUser) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-screen bg-gray-950 text-white relative px-4 select-none">
        {/* Subtle background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-md w-full bg-gray-900/60 backdrop-blur-md border border-gray-800 rounded-2xl p-8 flex flex-col items-center text-center shadow-2xl relative z-10">
          <div className="w-16 h-16 bg-orange-500/10 rounded-2xl flex items-center justify-center border border-orange-500/20 mb-6 shadow-lg shadow-orange-500/5">
            <ShieldAlert className="w-8 h-8 text-orange-500" />
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-white mb-2">
            Access Required
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed">
            Please reach out to the administrator to request system access.
          </p>
        </div>

        {/* Bottom logout only */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20">
          <button
            onClick={() => handleLogout()}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gray-900/80 hover:bg-red-500/20 border border-gray-800 hover:border-red-500/30 text-gray-300 hover:text-red-400 text-xs font-semibold tracking-wider uppercase transition-all duration-200 shadow-lg active:scale-[0.98]"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-gray-50 dark:bg-gray-950 transition-colors duration-200">
      {/* Main content area */}
      <main className="flex-1 overflow-y-auto relative bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
        {/* Added pt-8 to clear the TopBar */}
        <div className="min-h-full">
          <Outlet />
        </div>
      </main>

      {/* Floating Dock at the bottom */}
      <Dock />
    </div>
  );
};

export default MainLayout;
